import React, { useEffect, useState } from 'react';
import ChatHeader from '@components/organisms/chat/chat-header';
import ChatContent from '@components/organisms/chat/chat-content';
import ChatForm from '@components/organisms/chat/chat-form';
import { ChatProvider, Mode } from '@context/chat.context';
import { Product } from '@interfaces/models/product';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useUser from '@hooks/user/use-user';
import { useFocusRestore } from '@hooks/a11y/use-focus-restore';
import Cookies from 'universal-cookie';
import { AppCookies } from '@enums/cookies';
import { cookieOptions } from '@components/bucketTest/bucket.utils';
import styles from './chat.module.scss';
import ChatOnboardingModal from './chat-onboarding-modal/chat-onboarding-modal';

export interface ChatProps {
  product: Product;
  addToCart: () => void;
  closeHandler: () => void;
  mode?: Mode;
}

const Chat: React.FC<ChatProps> = ({ product, addToCart, closeHandler, mode = 'popup' }) => {
  const { sendAnalyticEvent: sendChatAnalyticEvents } = useAnalyticEvents('chat');
  const { user } = useUser();
  const isInline = mode === 'inline';

  const [isSeller] = useState<boolean>(product.seller.id === user?.id);
  const [index, setIndex] = useState<number>(0);
  const [isChatModalOpened, setIsChatModalOpened] = useState<boolean>(false);
  const [onBoardingGotIt, setOnBoardingGotIt] = useState<boolean>(false);

  const cookies = new Cookies();

  // Restore focus to chat button when chat panel closes (popup mode only)
  // For popup mode, component is conditionally rendered, so it will unmount when closed
  useFocusRestore(!isInline);

  // Reusing the classname which we use it on react-modal
  useEffect(() => {
    if (isInline) {
      return;
    }
    document.body.classList.add('scroll-lock');
    return () => {
      document.body.classList.remove('scroll-lock');
    };
  }, [mode]);

  const trackingChat = (origin: 'report' | 'message_sent') => {
    const chatPart = isSeller ? 'mmao_seller' : 'product_buyer';
    sendChatAnalyticEvents('bs_chat', {
      chat_action_name: origin || '',
      chat_context: chatPart,
    });
    sendChatAnalyticEvents('bs_chat_send_message');
  };

  const acceptModal = () => {
    setIsChatModalOpened(false);
    cookies.set(AppCookies.USER_CHAT_CHAT_ONBOARDING_DONE, true, cookieOptions);
  };

  const openOnBoarding = (gotItText = false) => {
    setIsChatModalOpened(true);
    setOnBoardingGotIt(gotItText);
  };

  const isFirstTimeChatter = () => {
    if (cookies.get(AppCookies.USER_CHAT_CHAT_ONBOARDING_DONE) !== true) {
      openOnBoarding(false);
    }
  };

  // send custom event to braze
  useEffect(() => {
    isFirstTimeChatter();
    setTimeout(() => {
      if (window.appboy) {
        window.appboy.logCustomEvent('browsed_chat', {});
      }
    }, 1500);
  }, []);

  const reloadChat = () => {
    setIndex(index + 1);
  };

  return (
    <>
      {!isInline ? (
        <ChatProvider
          product={product}
          mode={mode}
          key={`chat-inline-${index}`}
        >
          <div className={styles.chatPopupContainer}>
            <ChatHeader
              addToCart={addToCart}
              closeHandler={closeHandler}
            />
            <ChatContent
              reloadChat={reloadChat}
              isSeller={isSeller}
              sellerInfo={product.seller}
              userId={user?.id}
              openChatFromLike={false}
              openChatOnBoardingModal={(gotItText = false) => openOnBoarding(gotItText)}
            />
            <ChatForm onSubmit={() => trackingChat('message_sent')} />
          </div>
          <ChatOnboardingModal
            isOpen={isChatModalOpened}
            onClose={acceptModal}
            gotItText={onBoardingGotIt}
          />
        </ChatProvider>
      ) : (
        <div className={styles.chatInlineContainer}>
          <ChatHeader
            addToCart={addToCart}
            closeHandler={closeHandler}
          />
          <ChatContent
            reloadChat={() => {
              window.location.reload();
            }}
            isSeller={isSeller}
            sellerInfo={product.seller}
            userId={user?.id}
            openChatFromLike={false}
            openChatOnBoardingModal={(gotItText = false) => openOnBoarding(gotItText)}
          />
          <ChatForm onSubmit={() => trackingChat('message_sent')} />
          <ChatOnboardingModal
            isOpen={isChatModalOpened}
            onClose={acceptModal}
            gotItText={onBoardingGotIt}
          />
        </div>
      )}
    </>
  );
};

export default Chat;
